import { memo } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import { BRIA_CREDITS } from '../../shared/services/briaService'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The Background remove card (Huanrui, 2026-10-27)
 * -------------------------------------------------------------
 * One picture wired in on the left; it comes out on the right with nothing
 * behind the subject. Bria RMBG 2.0 finds the subject; the subject is the
 * original's own pixels at its full size, only the cut-out's edge comes from
 * the model (ProjectCanvas, runBackground; routes/bria.ts).
 *
 * Run puts a frame with the loader to the right, wired to the card, and folds
 * the card. (It could also put a new background behind the subject, with Bria
 * Background Replace, for a day; that was taken out -- Huanrui.)
 */

export interface BackgroundNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  /** Going now, by a local id each. */
  runs?: string[]
  error?: string
  credits?: number
  madeCount?: number

  /* handed in by the board, never stored */
  wired?: boolean
  onEdit?: (patch: Partial<BackgroundNodeData>) => void
  onRun?: () => void
}

export const BackgroundNode = memo(function BackgroundNode({ data, selected }: NodeProps) {
  const card = data as BackgroundNodeData
  const isOpen = card.open !== false
  const wired = card.wired === true
  const mine = (card.runs ?? []).length
  const edit = (patch: Partial<BackgroundNodeData>) => card.onEdit?.(patch)
  const why = wired ? '' : 'Connect a picture first'

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'bgnode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        wired ? 'cnode--lit' : '',
        mine > 0 ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          <p className="xnode__hint">
            Comes out see-through behind the subject, at full size. The subject keeps the original’s own pixels.
          </p>
          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {isOpen || mine > 0 ? (
        <button
          type="button"
          className="cnode__run pnode__run nodrag"
          onClick={() => card.onRun?.()}
          disabled={Boolean(why) || !isOpen}
          title={why || 'Take the background away'}
        >
          {!isOpen ? (
            'Running'
          ) : (
            <>
              Run
              <span className="pnode__credits">about {BRIA_CREDITS.remove} credits</span>
            </>
          )}
          {mine > 0 ? <span className="pnode__credits">· {mine} going</span> : null}
        </button>
      ) : null}

      {mine === 0 && card.credits ? (
        <span className="pnode__spent">
          {card.madeCount ?? 0} made · {card.credits} credits
        </span>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
